import React, { useEffect, useState } from "react";
import { Box, ButtonBase, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import PersonRounded from "@mui/icons-material/PersonRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import User from "../../../../api/User";
import { useCookies } from "react-cookie";
import TabHeader from "../../../TabHeader";
import { useNavigate } from "react-router-dom";
import Waitlist from "./Waitlist";
import CreateClientModal from "../../../Modals/CreateClientModal";
import { TAG_COLORS } from "../../../../utils/tagColors";
import EmptyListState from "../../../EmptyListState";
import SearchBar from "../../../SearchBar";
import TagFilterBar from "../../../TagFilterBar";
import { FONTS } from "../../../../constants/styles";
import { APP_MAX_WIDTH } from "../../../../constants/layout";

const tagsColors = TAG_COLORS;

const UserSelect = () => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const navigate = useNavigate();

    const [cookies] = useCookies(["uuid", "brand_id"]);
    const { uuid, brand_id } = cookies;

    const [tags, setTags] = useState<string[]>([]);
    const [selectedTag, setSelectedTag] = useState<string>("All");
    const [users, setUsers] = useState<User[]>([]);
    const [categorizedUsers, setCategorizedUsers] = useState<
        { letter: string; users: User[] }[]
    >([]);
    const [isUserCreationModalOpen, setIsUserCreationModalOpen] =
        useState(false);

    const [searchTerm, setSearchTerm] = useState("");

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const isPreselected =
            sessionStorage.getItem("booking_user_preselected") === "true";
        const preselectedUserId = sessionStorage.getItem("user_id");
        const preselectedServiceId = sessionStorage.getItem("service_id");

        if (!isPreselected) {
            // Clean the cookies up in the beginning of the flow
            sessionStorage.removeItem("user_id");
            sessionStorage.removeItem("service_id");
            sessionStorage.removeItem("booking_id");
            sessionStorage.removeItem("isNewUser");
        }

        const start = sessionStorage.getItem("slot_start");
        const end = sessionStorage.getItem("slot_end");
        if (start === null || end === null) {
            navigate("/calendar");
            return;
        }

        if (isPreselected && preselectedUserId) {
            // Client-first flow: client (and optionally service) is already
            // pre-selected from the profile screen. Skip user selection and
            // jump to the next pending step.
            sessionStorage.removeItem("booking_user_preselected");
            if (preselectedServiceId) {
                navigate("/calendar/add/confirmation");
            } else {
                navigate("/calendar/add/service");
            }
            return;
        }

        if (!uuid || !brand_id) return;

        // Fetch users
        setLoading(true);
        User.list(uuid, brand_id)
            .then((res) => setUsers(res ?? []))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [uuid, brand_id, navigate, selectedTag]);

    useEffect(() => {
        // Generate tag list based on user statuses
        const uniqueTags = Array.from(
            new Set(users.map((user) => user.status).filter(Boolean))
        );

        // Sort fetched tags based on TAG_COLORS order
        const sortedTags = uniqueTags.sort(
            (a, b) =>
                tagsColors.findIndex((tagObj) => tagObj.tag === a) -
                tagsColors.findIndex((tagObj) => tagObj.tag === b)
        );

        // Final order of tags (to keep it consistent)
        const orderedTags = ["All", "Waitlist", ...sortedTags];
        setTags(orderedTags);
    }, [users]);

    useEffect(() => {
        let categorizedUsers: { letter: string; users: User[] }[] = [];

        for (const user of users.filter((x) => {
            const matchesTag =
                selectedTag === "All"
                    ? true
                    : selectedTag === "Waitlist"
                    ? false
                    : x.status === selectedTag;
            const matchesSearch = x.name
                .toLowerCase()
                .includes(searchTerm.toLowerCase());
            return matchesTag && matchesSearch;
        })) {
            const names = user.name.split(" ");
            let letter: string;

            // Check if the user has a surname, fallback to the first letter of the first name
            if (names.length > 1 && names[names.length - 1].length > 0) {
                letter = names[names.length - 1][0].toUpperCase();
            } else {
                letter = names[0][0].toUpperCase();
            }

            const index = categorizedUsers.findIndex(
                (x) => x.letter.toUpperCase() === letter
            );
            if (index >= 0) {
                categorizedUsers[index].users.push(user);
            } else {
                categorizedUsers.push({ letter, users: [user] });
            }
        }

        setCategorizedUsers(
            categorizedUsers.sort((a, b) => a.letter.localeCompare(b.letter))
        );
    }, [users, selectedTag, searchTerm]);

    const onSelectUser = (user: User) => {
        sessionStorage.setItem("user_id", user.key);
        navigate("/calendar/add/service");
    };

    const onSelectWaitlistEntry = (entry: {
        userKey: string;
        serviceKey: string;
        bookingKey: string;
    }) => {
        sessionStorage.setItem("user_id", entry.userKey);
        sessionStorage.setItem("service_id", entry.serviceKey);
        sessionStorage.setItem("booking_id", entry.bookingKey);
        navigate("/calendar/add/confirmation");
    };

    const handleTagChange = (tag: string) => {
        if (tag === selectedTag) return; // Avoid redundant updates
        setSelectedTag(tag);
    };

    const onUserCreate = async (name: string) => {
        try {
            const userKey = await User.register(
                uuid,
                brand_id,
                "",
                name,
                "",
                "",
                "Ordinary",
                ""
            );
            if (!userKey) throw Error("No user key returned");
            sessionStorage.setItem("user_id", userKey);
            sessionStorage.setItem("isNewUser", "true");
            navigate("/calendar/add/service");
        } catch (err) {
            console.error(err);
        }
    };

    // Global stagger index across all groups
    let globalIndex = 0;

    // Calculate horizontal position of the FAB
    const fabRight =
        typeof window !== "undefined" && window.visualViewport
            ? window.visualViewport.width > APP_MAX_WIDTH
                ? `${APP_MAX_WIDTH / 10}px`
                : "9vw"
            : "9vw";

    return (
        <>
            <TabHeader
                name="Pick a client"
                onClick={() => navigate("/calendar")}
            />
            <TagFilterBar
                tags={tags}
                selectedTag={selectedTag}
                onSelectTag={handleTagChange}
            />
            <Box sx={{ px: 2, pb: 1 }}>
                <SearchBar
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                />
            </Box>
            <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0, display: "flex", flexDirection: "column" }}>
            {selectedTag === "Waitlist" ? (
                <Waitlist
                    accountKey={uuid}
                    brandKey={brand_id}
                    onSelect={onSelectWaitlistEntry}
                />
            ) : !loading && categorizedUsers.length === 0 ? (
                users.length === 0 ? (
                    <EmptyListState
                        primaryText="No users yet"
                        button={{
                            text: "Add First User",
                            icon: (
                                <AddRounded
                                    sx={{ fontSize: 18, color: primary }}
                                />
                            ),
                            onClick: () => setIsUserCreationModalOpen(true),
                        }}
                    />
                ) : (
                    <EmptyListState primaryText="No users found" />
                )
            ) : (
                <Stack
                    spacing={3}
                    sx={{
                        px: 2,
                        pb: "200px",
                        "@keyframes fadeSlideIn": {
                            from: {
                                opacity: 0,
                                transform: "translateY(12px)",
                            },
                            to: {
                                opacity: 1,
                                transform: "translateY(0)",
                            },
                        },
                        animation: "fadeSlideIn 0.3s ease-out",
                    }}
                >
                    {categorizedUsers.map((category) => {
                        const groupStartIndex = globalIndex;
                        globalIndex += category.users.length;

                        return (
                            <Stack key={category.letter} spacing={0.5}>
                                {/* Alphabet header */}
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    sx={{ px: 0.5, mb: 1 }}
                                >
                                    <Typography
                                        className="no-translation"
                                        fontFamily={FONTS.display}
                                        fontWeight={700}
                                        fontSize="14px"
                                        color={primary}
                                        sx={{ flexShrink: 0 }}
                                    >
                                        {category.letter}
                                    </Typography>
                                    <Box
                                        flex={1}
                                        height="1px"
                                        bgcolor={alpha(primary, 0.1)}
                                    />
                                </Stack>

                                {/* User rows */}
                                {category.users.map((user, i) => (
                                    <React.Fragment key={user.key}>
                                        <UserBlock
                                            user={user}
                                            index={groupStartIndex + i}
                                            onNext={onSelectUser}
                                        />
                                        {i < category.users.length - 1 && (
                                            <Box
                                                sx={{
                                                    height: "1px",
                                                    bgcolor:
                                                        "rgba(0,0,0,0.06)",
                                                    ml: "60px",
                                                }}
                                            />
                                        )}
                                    </React.Fragment>
                                ))}
                            </Stack>
                        );
                    })}
                </Stack>
            )}
            </Box>
            {/* Floating Add Button */}
            {selectedTag !== "Waitlist" && categorizedUsers.length > 0 && (
                <Box
                    position="fixed"
                    bottom="80px"
                    left="50%"
                    zIndex={5}
                    sx={{
                        transform: "translateX(-50%)",
                        width: "100%",
                        maxWidth: APP_MAX_WIDTH,
                        display: "flex",
                        justifyContent: "flex-end",
                    }}
                >
                    <ButtonBase
                        sx={{
                            width: 52,
                            height: 52,
                            borderRadius: "16px",
                            bgcolor: primary,
                            marginRight: fabRight,
                            boxShadow: `0 4px 14px ${alpha(primary, 0.35)}`,
                            transition: "transform 0.15s ease",
                            "&:active": { transform: "scale(0.92)" },
                            "@keyframes fadeSlideUp": {
                                from: {
                                    opacity: 0,
                                    transform: "translateY(16px)",
                                },
                                to: {
                                    opacity: 1,
                                    transform: "translateY(0)",
                                },
                            },
                            animation: "fadeSlideUp 0.4s ease-out 0.3s both",
                        }}
                        onClick={() => setIsUserCreationModalOpen(true)}
                    >
                        <AddRounded sx={{ color: "white", fontSize: 24 }} />
                    </ButtonBase>
                </Box>
            )}
            <CreateClientModal
                open={isUserCreationModalOpen}
                onClose={() => setIsUserCreationModalOpen(false)}
                onSubmit={(name) => onUserCreate(name)}
            />
        </>
    );
};

const UserBlock = (props: {
    user: User;
    index: number;
    onNext: (val: User) => void;
}) => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;

    const { user, index, onNext } = props;

    const formatName = (x: string) => {
        const names = x.split(" ");
        if (names.length > 1) {
            return `${names[0]} ${names.slice(1).join(" ")}`;
        }
        return names[0];
    };

    const statusColor =
        tagsColors.find((x) => x.tag === user.status)?.color ?? primary;

    return (
        <ButtonBase
            sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                py: 1,
                px: 0.5,
                borderRadius: "12px",
                "@keyframes fadeSlideUp": {
                    from: { opacity: 0, transform: "translateY(10px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                },
                animation: `fadeSlideUp 0.25s ease-out ${index * 30}ms both`,
            }}
            onClick={() => onNext(user)}
        >
            {/* Avatar */}
            {user.image ? (
                <Box
                    sx={{
                        width: 44,
                        minWidth: 44,
                        height: 44,
                        borderRadius: "14px",
                        backgroundImage: `url(${user.image})`,
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "cover",
                        flex: "0 0 auto",
                    }}
                />
            ) : (
                <Box
                    sx={{
                        width: 44,
                        minWidth: 44,
                        height: 44,
                        borderRadius: "14px",
                        bgcolor: alpha(primary, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: "0 0 auto",
                    }}
                >
                    <PersonRounded
                        sx={{ fontSize: 22, color: primary, opacity: 0.6 }}
                    />
                </Box>
            )}

            {/* Info */}
            <Stack
                direction="column"
                flex={1}
                justifyContent="center"
                alignItems="flex-start"
                spacing={0.5}
                ml={1.5}
                sx={{ overflow: "hidden" }}
            >
                <Typography
                    className="no-translation"
                    fontFamily={FONTS.body}
                    fontWeight={600}
                    fontSize="15px"
                    lineHeight={1.3}
                    noWrap
                >
                    {formatName(user.name)}
                </Typography>
                {user.status && (
                    <Box
                        sx={{
                            bgcolor: alpha(statusColor, 0.1),
                            borderRadius: "10px",
                            px: 0.75,
                            py: 0.25,
                        }}
                    >
                        <Typography
                            fontWeight={500}
                            fontSize="11px"
                            lineHeight={1.3}
                            color={statusColor}
                        >
                            {user.status}
                        </Typography>
                    </Box>
                )}
            </Stack>
        </ButtonBase>
    );
};

export default UserSelect;
